import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Box, TextField } from '@mui/material'
import QRCode from 'react-qr-code'

import DefaultModal from '../modal/DefaultModal/DefaultModal'
import { RoundedButton } from '../ui/roundButton'
import {
	checkScoutAccountConnection,
	formatPhoneNumber,
	getScoutAccountQr,
} from '../../services/scout'
import { openSupportForm } from '../../services/support'

const isValidEmail = (value = '') => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || '').trim())

const getQrCodeFromConnectionPayload = (data = {}) => (
	data.code ||
	data.Code ||
	data.qrcode?.code ||
	data.qrCode?.code ||
	''
)

const getPairingCodeFromConnectionPayload = (data = {}) => (
	data.pairingCode ||
	data.PairingCode ||
	data.qrcode?.pairingCode ||
	data.qrCode?.pairingCode ||
	''
)

const FirstConnectionGate = ({ account, onConnected, onSendQrByEmail, showSnackbar }) => {
	const [qrcode, setQrcode] = useState('')
	const [pairingCode, setPairingCode] = useState('')
	const [qrcodeLoading, setQrcodeLoading] = useState(false)
	const [emailModalOpen, setEmailModalOpen] = useState(false)
	const [videoModalOpen, setVideoModalOpen] = useState(false)
	const [email, setEmail] = useState('')
	const [emailSending, setEmailSending] = useState(false)
	const connectionResolvedRef = useRef(false)
	const accountId = account?.id
	const formattedPhoneNumber = formatPhoneNumber(account?.phone_number)

	const loadQrCode = useCallback(async () => {
		if (!accountId) {
			return
		}

		try {
			setQrcodeLoading(true)
			const data = await getScoutAccountQr({ accountId })
			setQrcode(getQrCodeFromConnectionPayload(data))
			setPairingCode(getPairingCodeFromConnectionPayload(data))
		} catch (error) {
			if (!connectionResolvedRef.current) {
				showSnackbar?.({ message: error.message || 'Não foi possível gerar o QR Code.', severity: 'error' })
			}
		} finally {
			setQrcodeLoading(false)
		}
	}, [accountId, showSnackbar])

	const formattedPairingCode = useMemo(() => (
		String(pairingCode || '')
			.replace(/[\s-]/g, '')
			.replace(/(.{4})(?=.)/g, '$1-')
	), [pairingCode])

	const handleCopyPairingCode = async () => {
		if (!pairingCode) {
			return
		}

		try {
			await navigator.clipboard.writeText(String(pairingCode).replace(/[\s-]/g, ''))
			showSnackbar?.({ message: 'Código de pareamento copiado.', severity: 'success' })
		} catch (error) {
			showSnackbar?.({ message: 'Não foi possível copiar o código de pareamento.', severity: 'error' })
		}
	}

	useEffect(() => {
		if (!accountId) {
			return undefined
		}

		connectionResolvedRef.current = false
		loadQrCode()

		const qrInterval = setInterval(loadQrCode, 45 * 1000)
		const connectionInterval = setInterval(async () => {
			if (connectionResolvedRef.current) {
				return
			}

			try {
				const data = await checkScoutAccountConnection({ accountId })

				if (data?.state === 'open') {
					connectionResolvedRef.current = true
					clearInterval(qrInterval)
					clearInterval(connectionInterval)
					showSnackbar?.({ message: 'Número conectado com sucesso!', severity: 'success' })
					onConnected?.()
				}

				if (!connectionResolvedRef.current && data?.state === 'wrongNumber') {
					showSnackbar?.({ message: 'O número conectado não corresponde ao número monitorado.', severity: 'error' })
				}
			} catch (error) {
				console.error('Error checking Scout account connection', error)
			}
		}, 5 * 1000)

		return () => {
			clearInterval(qrInterval)
			clearInterval(connectionInterval)
		}
	}, [accountId, loadQrCode, onConnected, showSnackbar])

	const handleSendEmail = async () => {
		if (!isValidEmail(email)) {
			return
		}

		try {
			setEmailSending(true)
			await onSendQrByEmail?.(account, email.trim())
			showSnackbar?.({ message: 'QR Code enviado por e-mail com sucesso.', severity: 'success' })
			setEmail('')
			setEmailModalOpen(false)
		} catch (error) {
			showSnackbar?.({ message: error.message || 'Não foi possível enviar o QR Code por e-mail.', severity: 'error' })
		} finally {
			setEmailSending(false)
		}
	}

	return (
		<section className="scout-first-connection-gate">
			<div className="scout-panel scout-first-connection-gate__panel">
				<div className="scout-first-connection-gate__content">
					<div className="scout-first-connection-gate__progress">
						<div className="scout-first-connection-gate__progress-labels">
							<span>Etapa 2 de 3 - Conectar número</span>
							<span>Depois: ver primeiras conversas</span>
						</div>
						<div className="scout-first-connection-gate__progress-track">
							<span />
						</div>
					</div>

					<div className="scout-first-connection-gate__header">
						<h2>Falta só conectar 1 número para ver o Scout funcionando</h2>
						<p>
							Seu teste já está ativo. Escaneie o QR Code no WhatsApp do número monitorado ou conecte com código para começar a visualizar conversas, buscas e alertas em poucos minutos.
						</p>
					</div>

					<div className="scout-first-connection-gate__video-help">
						<div className="scout-first-connection-gate__video-help-text">
							<strong>Como conectar.</strong>
							<span>Veja um vídeo rápido com o procedimento. Você também pode continuar seguindo as instruções da tela.</span>
						</div>
						<RoundedButton rounded="outlined" onClick={() => setVideoModalOpen(true)}>
							Ver vídeo rápido
						</RoundedButton>
					</div>

					<div className="scout-first-connection-gate__body">
						<div className="scout-first-connection-gate__instructions">
							<p>No celular que será monitorado:</p>
							<ol>
								{[
									<>Abra o <strong>WhatsApp</strong></>,
									<>Vá em <strong>Menu</strong> ou <strong>Configurações</strong></>,
									<>Escolha <strong>Dispositivos conectados</strong></>,
									<>Clique em <strong>Conectar dispositivo</strong></>,
									<>Escaneie o QR Code ao lado ou conecte com o código abaixo</>,
								].map((step, index) => (
									<li key={index}>
										<span>{index + 1}.</span>
										<span>{step}</span>
									</li>
								))}
							</ol>
							<div className="scout-first-connection-gate__time-note">
								Leva menos de 2 minutos.
							</div>
						</div>

						<Box className="scout-first-connection-gate__connect">
							<Box className="scout-first-connection-gate__qr">
								{qrcode ? <QRCode size={220} value={qrcode} /> : <span className="scout-inline-feedback">{qrcodeLoading ? 'Gerando QR Code...' : 'QR Code indisponível'}</span>}
							</Box>
							<span className="scout-first-connection-gate__phone-hint">
								Use o WhatsApp do número<br />{formattedPhoneNumber}
							</span>
							<Box className="scout-first-connection-gate__pairing">
								<span>Código de pareamento</span>
								{pairingCode ? (
									<>
										<strong>{formattedPairingCode}</strong>
										<small>No WhatsApp, escolha conectar com número de telefone e informe este código.</small>
										<button type="button" onClick={handleCopyPairingCode}>Copiar código</button>
									</>
								) : (
									<small>{qrcodeLoading ? 'Gerando código...' : 'Erro ao gerar código de conexão.'}</small>
								)}
							</Box>
						</Box>
					</div>

					<div className="scout-first-connection-gate__benefits">
						<strong>Depois de conectar, você poderá:</strong>
						<ul>
							<li>Ver mensagens centralizadas</li>
							<li>Buscar conversas por palavra-chave</li>
							<li>Identificar riscos e falhas</li>
							<li>Acompanhar o WhatsApp corporativo com mais controle</li>
						</ul>
					</div>


					<div className="scout-first-connection-gate__info-cards">
						<div>
							<strong>Comece pequeno</strong>
							<p>Conecte apenas 1 número agora. Você pode adicionar outros depois.</p>
						</div>
						<div>
							<strong>Escolha o melhor número</strong>
							<p>Dê preferência ao número que mais conversa com clientes.</p>
						</div>
						<div>
							<strong>Valide na prática</strong>
							<p>A maioria das empresas começa conectando 1 número para testar o valor do Scout.</p>
						</div>
					</div>

					<div className="scout-first-connection-gate__warning">
						<strong>Importante:</strong> enquanto nenhum número estiver conectado, seu teste ainda não gera histórico, buscas, alertas ou evidências de controle.
					</div>

					<div className="scout-first-connection-gate__actions">
						<RoundedButton className="scout-first-connection-gate__action" onClick={loadQrCode} disabled={qrcodeLoading}>
							{qrcodeLoading ? 'Gerando conexão...' : 'Escaneie o QR Code para começar agora'}
						</RoundedButton>
						<div className="scout-first-connection-gate__links">
							<button type="button" onClick={() => setEmailModalOpen(true)}>
								Enviar instruções por e-mail
							</button>
							<button type="button" onClick={openSupportForm}>
								Preciso de ajuda para conectar
							</button>
						</div>
					</div>

					<p className="scout-first-connection-gate__footer-note">
						A conexão usa o recurso de dispositivos conectados do WhatsApp. O colaborador continua usando o aplicativo normalmente no celular. O Scout permite que a empresa visualize e organize as conversas do número autorizado.
					</p>
				</div>
			</div>

			{emailModalOpen ? (
				<DefaultModal
					title="Enviar QR Code por e-mail"
					handleClose={() => setEmailModalOpen(false)}
					maxWidth="xs"
					action={
						<Box width="100%" display="flex" gap="1rem" sx={{ justifyContent: 'space-between', marginTop: '1rem' }}>
							<RoundedButton rounded="outlined" onClick={() => setEmailModalOpen(false)} sx={{ width: '100%' }}>
								Cancelar
							</RoundedButton>
							<RoundedButton sx={{ width: '100%' }} disabled={!isValidEmail(email) || emailSending} onClick={handleSendEmail}>
								{emailSending ? 'Enviando...' : 'Enviar'}
							</RoundedButton>
						</Box>
					}
				>
					<Box display="flex" gap="1rem" flexDirection="column">
						<Box className="bg-gray-100 rounded-xl p-4 text-center" sx={{ background: '#ebf4f6' }}>
							<Box className="font-bold text-sm text-gray-900">{account?.name}</Box>
							<Box className="text-sm text-gray-400">{formatPhoneNumber(account?.phone_number)}</Box>
						</Box>
						<TextField
							fullWidth
							size="small"
							type="email"
							label="E-mail"
							placeholder="email@empresa.com"
							value={email}
							onChange={(event) => setEmail(event.target.value)}
							error={Boolean(email) && !isValidEmail(email)}
							helperText={Boolean(email) && !isValidEmail(email) ? 'Informe um e-mail válido.' : ''}
						/>
					</Box>
				</DefaultModal>
			) : null}

			{videoModalOpen ? (
				<DefaultModal
					title="Como conectar o WhatsApp"
					subtitle="Veja o passo a passo para conectar seu número ao Scout."
					handleClose={() => setVideoModalOpen(false)}
					maxWidth="sm"
					action={
						<RoundedButton onClick={() => setVideoModalOpen(false)}>
							Entendi, continuar
						</RoundedButton>
					}
				>
					<Box className="scout-first-connection-gate__video-modal">
						<video
							src="https://cf-templates-bum1ctwi7lwa-us-east-1.s3.us-east-1.amazonaws.com/SCOUT+-+Design.mp4"
							controls
						/>
					</Box>
				</DefaultModal>
			) : null}
		</section>
	)
}

export default FirstConnectionGate
